import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faChevronDown,
  faChevronUp,
  faTimes,
} from '@fortawesome/free-solid-svg-icons'
import searchIcon from 'Assets/Images/BuscarCotizacion/search.svg'
import { getValueFromPath } from 'Utils/Helpers/getValueFromPath'
import PropTypes from 'prop-types'
import { t } from 'i18next'
import './CustomDropdown.css'

const CustomDropdown = ({
  selectedItem = null,
  items = [],
  onSelect,
  placeholder = t('CUSTOM_DROPDOWN_T.DEFAULT_PLACEHOLDER'),
  showSearch = false,
  searchPlaceholder = t('CUSTOM_DROPDOWN_T.DEFAULT_SEARCH_PLACEHOLDER'),
  displayKey = 'label',
  keyExtractor,
  renderSelected,
  renderItem,
  renderChip,
  searchKeys,
  wrapperStyle = {},
  compareItems = (a, b) => a === b,
  isLoading = false,
  multiple = false,
}) => {
  const buttonRef = useRef(null)
  const dropdownRef = useRef(null)
  const chipsContainerRef = useRef(null)
  const hiddenMeasureRef = useRef(null)
  const measurementTimeoutRef = useRef(null)

  const [searchTerm, setSearchTerm] = useState('')
  const [showDropdown, setShowDropdown] = useState(false)
  const [displayState, setDisplayState] = useState({
    items: [],
    count: 0,
    totalCount: 0,
  })
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 'auto',
  })
  const prevSelectedRef = useRef([])

  const internalSelected = useMemo(() => {
    if (!multiple) {
      if (!selectedItem) return null

      // find the actual item using compareItems
      const found = items.find(item => compareItems(item, selectedItem))
      return found ?? null
    }

    return Array.isArray(selectedItem) ? selectedItem : []
  }, [multiple, selectedItem, items, compareItems])

  // search keys
  const keysToSearch =
    Array.isArray(searchKeys) && searchKeys.length > 0
      ? searchKeys
      : [displayKey]
  const normalizeText = text =>
    text
      ?.toString()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase() ?? ''

  const filteredItems = useMemo(() => {
    const s = normalizeText(searchTerm)
    if (!s) return items
    return items.filter(item =>
      keysToSearch.some(k =>
        normalizeText(getValueFromPath(item, k) ?? '').includes(s)
      )
    )
  }, [items, searchTerm, keysToSearch])

  // Function to get a unique key for an item
  const getItemKey = useCallback(
    (item, index) => {
      if (keyExtractor) {
        return keyExtractor(item, index)
      }
      // Fallback: Try to use common properties
      return item?.id ?? item?.[displayKey] ?? `item-${index}`
    },
    [keyExtractor, displayKey]
  )

  // Calculate how many chips fit synchronously
  const measureAndUpdate = useCallback(() => {
    if (
      !multiple ||
      !Array.isArray(internalSelected) ||
      internalSelected.length === 0
    ) {
      setDisplayState({ items: [], count: 0, totalCount: 0 })
      prevSelectedRef.current = []
      return
    }

    const btn = buttonRef.current
    const measureContainer = hiddenMeasureRef.current

    if (!btn || !measureContainer) {
      setDisplayState(prev => {
        if (
          prev.totalCount === internalSelected.length &&
          prev.count === finalCount
        ) {
          return prev
        }

        return {
          items: internalSelected.slice(0, finalCount),
          count: finalCount,
          totalCount: internalSelected.length,
        }
      })
      prevSelectedRef.current = internalSelected
      return
    }

    // Wait for the measurement container to have the elements rendered
    const chips = measureContainer.querySelectorAll('.custom-dropdown-chip')
    if (chips.length === 0 || chips.length !== internalSelected.length) {
      // If there are no chips yet or they don't match, keep the previous state and retry
      if (measurementTimeoutRef.current) {
        clearTimeout(measurementTimeoutRef.current)
      }
      measurementTimeoutRef.current = setTimeout(() => {
        measureAndUpdate()
      }, 10)
      return
    }

    const buttonWidth = btn.getBoundingClientRect().width
    const chevronWidth = 40
    const counterWidth = 50
    const gap = 6
    const availableWidth = buttonWidth - chevronWidth - 20

    let usedWidth = 0
    let count = 0

    for (let i = 0; i < chips.length; i++) {
      const chipWidth = chips[i].offsetWidth
      if (chipWidth === 0) {
        // If it's not measured, retry
        if (measurementTimeoutRef.current) {
          clearTimeout(measurementTimeoutRef.current)
        }
        measurementTimeoutRef.current = setTimeout(() => {
          measureAndUpdate()
        }, 10)
        return
      }

      const remainingItems = internalSelected.length - (count + 1)
      const needsCounter = remainingItems > 0

      const widthIfAdded = usedWidth + chipWidth + (count > 0 ? gap : 0)
      const totalWidth = widthIfAdded + (needsCounter ? counterWidth + gap : 0)

      if (totalWidth <= availableWidth) {
        usedWidth = widthIfAdded
        count++
      } else {
        break
      }
    }

    const finalCount = Math.max(1, count)
    setDisplayState({
      items: internalSelected.slice(0, finalCount),
      count: finalCount,
      totalCount: internalSelected.length,
    })
    prevSelectedRef.current = internalSelected
  }, [multiple, internalSelected])

  // Measure when the selection changes\
  useEffect(() => {
    if (!multiple || internalSelected.length === 0) return

    const raf = requestAnimationFrame(measureAndUpdate)
    return () => cancelAnimationFrame(raf)
  }, [multiple, internalSelected, measureAndUpdate])

  // recalculate en resize
  useEffect(() => {
    if (!multiple || !internalSelected?.length) return

    let rafId = 0
    const onResize = () => {
      if (rafId) cancelAnimationFrame(rafId)
      rafId = requestAnimationFrame(() => {
        measureAndUpdate()
      })
    }

    window.addEventListener('resize', onResize)
    return () => {
      if (rafId) cancelAnimationFrame(rafId)
      window.removeEventListener('resize', onResize)
    }
  }, [multiple, internalSelected, measureAndUpdate])

  // portal container (modal support)
  const getPortalContainer = useCallback(() => {
    let el = buttonRef.current?.parentElement
    while (el) {
      if (
        el.classList?.contains('modal-content') ||
        el.classList?.contains('MuiModal-root')
      )
        return el
      el = el.parentElement
    }
    return document.body
  }, [])

  // compute dropdown coords
  const computeAndSetPosition = useCallback(portalContainer => {
    const btn = buttonRef.current
    if (!btn || !portalContainer) return
    const buttonRect = btn.getBoundingClientRect()
    const parentRect = btn.offsetParent?.getBoundingClientRect?.()
    const width = parentRect?.width ?? buttonRect.width

    if (portalContainer === document.body) {
      setDropdownPosition({
        top: buttonRect.bottom + window.scrollY,
        left: parentRect
          ? parentRect.left + window.scrollX
          : buttonRect.left + window.scrollX,
        width,
      })
    } else {
      const containerRect = portalContainer.getBoundingClientRect()
      const top =
        buttonRect.bottom - containerRect.top + (portalContainer.scrollTop || 0)
      const left = parentRect
        ? parentRect.left -
          containerRect.left +
          (portalContainer.scrollLeft || 0)
        : buttonRect.left -
          containerRect.left +
          (portalContainer.scrollLeft || 0)
      setDropdownPosition({ top, left, width })
    }
  }, [])

  // show/hide dropdown
  const handleShowDropdown = useCallback(
    value => {
      const portal = getPortalContainer()
      if (value && buttonRef.current) computeAndSetPosition(portal)
      if (showDropdown) setSearchTerm('')
      setShowDropdown(value)
    },
    [computeAndSetPosition, getPortalContainer, showDropdown]
  )

  // observe scroll/resize while open
  useEffect(() => {
    if (!showDropdown) return
    const portal = getPortalContainer()
    const onScrollOrResize = () => computeAndSetPosition(portal)
    try {
      portal.addEventListener('scroll', onScrollOrResize, { passive: true })
    } catch {}
    window.addEventListener('scroll', onScrollOrResize, { passive: true })
    window.addEventListener('resize', onScrollOrResize)
    let mo
    try {
      mo = new MutationObserver(onScrollOrResize)
      mo.observe(portal, { childList: true, subtree: true, attributes: true })
    } catch (e) {}
    return () => {
      try {
        portal.removeEventListener('scroll', onScrollOrResize)
      } catch {}
      window.removeEventListener('scroll', onScrollOrResize)
      window.removeEventListener('resize', onScrollOrResize)
      if (mo) mo.disconnect()
    }
  }, [showDropdown, computeAndSetPosition, getPortalContainer])

  // click outside to close
  useEffect(() => {
    const handleClickOutside = e => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target)
      ) {
        handleShowDropdown(false)
      }
    }
    if (showDropdown) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showDropdown, handleShowDropdown])

  // item click handler
  const handleItemClick = item => {
    if (multiple) {
      onSelect(item)
    } else {
      onSelect(item)
      setShowDropdown(false)
    }
  }

  // Hidden measurement container
  const renderMeasurementChips = () => {
    if (
      !multiple ||
      !Array.isArray(internalSelected) ||
      internalSelected.length === 0
    ) {
      return null
    }

    return (
      <div
        ref={hiddenMeasureRef}
        style={{
          position: 'absolute',
          visibility: 'hidden',
          pointerEvents: 'none',
          display: 'flex',
          gap: '6px',
          left: '-9999px',
          top: '-9999px',
          whiteSpace: 'nowrap',
        }}>
        {internalSelected.map((item, idx) => {
          const key = getItemKey(item, idx)
          return (
            <div key={key} className="custom-dropdown-chip">
              <span className="chip-text">
                {renderChip
                  ? renderChip(item)
                  : renderSelected
                  ? renderSelected(item)
                  : item[displayKey]}
              </span>
              <FontAwesomeIcon icon={faTimes} className="chip-remove-icon" />
            </div>
          )
        })}
      </div>
    )
  }

  // render chips
  const renderChips = () => {
    if (
      !multiple ||
      !Array.isArray(internalSelected) ||
      internalSelected.length === 0
    ) {
      return (
        <p className="custom-dropdown-button-text-unselect">{placeholder}</p>
      )
    }

    // Use the displayState, which is only updated after measuring
    // If the displayState is not synchronized with internalSelected, show the previous state
    const isDisplayStateSynced =
      displayState.totalCount === internalSelected.length

    const itemsToShow = isDisplayStateSynced
      ? displayState.items
      : prevSelectedRef.current.slice(0, displayState.count)
    const currentTotal = isDisplayStateSynced
      ? internalSelected.length
      : prevSelectedRef.current.length
    const hiddenCount =
      currentTotal - Math.min(displayState.count, itemsToShow.length)

    // Fallback: If there is nothing to display, show at least the first item
    const safeItemsToShow =
      itemsToShow.length > 0 ? itemsToShow : internalSelected.slice(0, 1)
    const safeHiddenCount =
      itemsToShow.length > 0
        ? hiddenCount
        : Math.max(0, internalSelected.length - 1)

    return (
      <div className="custom-dropdown-chips-wrapper">
        <div
          className="custom-dropdown-chips-container"
          ref={chipsContainerRef}>
          {safeItemsToShow.map((item, idx) => {
            const key = getItemKey(item, idx)
            return (
              <div key={key} className="custom-dropdown-chip">
                <span className="chip-text">
                  {renderChip
                    ? renderChip(item)
                    : renderSelected
                    ? renderSelected(item)
                    : item[displayKey]}
                </span>
                <FontAwesomeIcon
                  icon={faTimes}
                  className="chip-remove-icon"
                  onClick={e => {
                    e.stopPropagation()
                    handleItemClick(item)
                  }}
                />
              </div>
            )
          })}
          {safeHiddenCount > 0 && (
            <div className="custom-dropdown-chip counter-chip">
              +{safeHiddenCount}
            </div>
          )}
        </div>
      </div>
    )
  }

  const isItemSelected = item => {
    if (!multiple) return compareItems(item, internalSelected)
    if (!Array.isArray(internalSelected)) return false

    return internalSelected.some(sel => compareItems(sel, item))
  }

  // dropdown list content
  const dropdownContent = (
    <div
      ref={dropdownRef}
      className={
        showDropdown ? 'custom-dropdown-list' : 'custom-dropdown-list_hidden'
      }
      style={{
        position: 'absolute',
        top: dropdownPosition.top ?? 0,
        left: dropdownPosition.left ?? 0,
        width: dropdownPosition.width ?? 'auto',
        minWidth: dropdownPosition.width ?? 'auto',
        maxWidth: dropdownPosition.width ?? 'auto',
        boxSizing: 'border-box',
        zIndex: 9999,
      }}
      onMouseLeave={() => {
        if (!multiple) handleShowDropdown(false)
      }}>
      {showSearch && (
        <div className="custom-dropdown-container-search">
          <div className="custom-dropdown-search-bar">
            <img
              alt="search"
              src={searchIcon}
              className="custom-dropdown-search-icon"
            />
            <input
              type="text"
              placeholder={searchPlaceholder}
              data-testid="input-search-custom-dropdown"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      )}

      <ul className="custom-dropdown-ul">
        {filteredItems.map((item, index) => {
          const key = getItemKey(item, index)
          const sel = isItemSelected(item)
          return (
            <button
              key={key}
              type="button"
              className="custom-dropdown-li"
              data-testid={`btn-item-custom-dropdown-${
                item.id || item[displayKey]
              }`}
              onClick={() => handleItemClick(item)}>
              {renderItem ? renderItem(item, sel) : item[displayKey]}
            </button>
          )
        })}
      </ul>
    </div>
  )

  const triggerContent = multiple ? (
    renderChips()
  ) : internalSelected ? (
    renderSelected ? (
      <p>{renderSelected(internalSelected)}</p>
    ) : (
      <p className="custom-dropdown-button-text-unselect">
        {internalSelected[displayKey]}
      </p>
    )
  ) : (
    <p className="custom-dropdown-button-text-unselect">{placeholder}</p>
  )

  const portalContainer = getPortalContainer()

  return (
    <>
      <div className="custom-dropdown" style={{ ...wrapperStyle }}>
        <button
          ref={buttonRef}
          type="button"
          style={{ width: wrapperStyle?.width ?? '100%' }}
          className={`custom-dropdown-button ${
            multiple ? 'multi-selection' : ''
          }`}
          data-testid="btn-custom-dropdown"
          onClick={() => handleShowDropdown(!showDropdown)}>
          {isLoading ? (
            <span>
              {t('CUSTOM_DROPDOWN_T.LOADING_MESSAGE') || 'Loading...'}
            </span>
          ) : (
            triggerContent
          )}

          <FontAwesomeIcon
            className="custom-dropdown-down-icon"
            icon={showDropdown ? faChevronUp : faChevronDown}
          />
        </button>

        {/* Hidden measurement container */}
        {renderMeasurementChips()}
      </div>

      {showDropdown &&
        (portalContainer === document.body
          ? createPortal(dropdownContent, portalContainer)
          : createPortal(dropdownContent, portalContainer))}
    </>
  )
}

CustomDropdown.propTypes = {
  selectedItem: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
    PropTypes.array,
  ]),
  items: PropTypes.array.isRequired,
  onSelect: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  showSearch: PropTypes.bool,
  searchPlaceholder: PropTypes.string,
  displayKey: PropTypes.string,
  keyExtractor: PropTypes.func,
  renderSelected: PropTypes.func,
  renderItem: PropTypes.func,
  renderChip: PropTypes.func,
  searchKeys: PropTypes.arrayOf(PropTypes.string),
  wrapperStyle: PropTypes.object,
  compareItems: PropTypes.func,
  isLoading: PropTypes.bool,
  multiple: PropTypes.bool,
}

export default CustomDropdown
